/**
 * Select — eigenes Dropdown-Component, weil natives <select> auf OS-Ebene
 * gerendert wird (Windows-Grau-Highlight, Schriftgröße-Klau, Z-Index-Chaos
 * im Modal-Stack). Dieses Component baut den Trigger im .fd-input-Look
 * und ein eigenes Popup-Panel im Design-System.
 *
 * GENESIS: 1:1 portiert aus flawdesk-de/src/components/ui/Select.jsx (#1101).
 * Wenn sich Funktionalität ändert, BEIDE Kopien syncen (kein npm-Paket weil
 * monorepo-Setup das nicht hergibt). Die zwei Files sollen byte-identisch
 * bleiben — nur die CSS-Tokens hinter den Klassen unterscheiden sich.
 *
 * API (drop-in für die meisten <select>-Stellen):
 *   <Select value={x} onChange={fn} options={[{value, label, disabled?}]}
 *           placeholder="…" disabled={bool} style={…} className="…" />
 *
 * Verhalten:
 * - Click auf Trigger → Popup, Trigger erhält aria-expanded
 * - Keyboard: ↓/↑ navigiert; Enter wählt; Esc schließt; Tab schließt sauber;
 *   Home/End springen ans Ende; getipte Buchstaben jumpen zur passenden Option
 * - Bei zu wenig Platz nach unten flippt das Popup nach oben (auto)
 * - Klick außerhalb schließt — wir hängen den Listener nur on-open auf,
 *   damit's keine Background-Kosten gibt
 * - useLayoutEffect synct die Popup-Position vor dem Paint, damit's nicht
 *   ein Frame lang an falscher Stelle aufblitzt
 *
 * Bewusst weggelassen: Portal. Würde Z-Index zwar isolieren, dafür müssten
 * wir aber Position-Updates bei Scroll/Resize selbst pumpen. Für unsere
 * Use-Cases (Forms in Panels) reicht position:absolute innerhalb des
 * Container-Stacking-Context.
 */

import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'

const ChevronIcon = () => (
  <svg className="fd-select__chevron" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
       aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

const CheckIcon = () => (
  <svg className="fd-select__check" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
       aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

export default function Select({
  value,
  onChange,
  options = [],
  placeholder = '— bitte wählen —',
  disabled = false,
  className = '',
  style,
  id,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false)
  const [direction, setDirection] = useState('down')
  const [activeIndex, setActiveIndex] = useState(-1)

  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const typeBufferRef = useRef({ buffer: '', timer: null })

  const selectedIndex = options.findIndex(o => String(o.value) === String(value ?? ''))
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null

  const openMenu = useCallback(() => {
    if (disabled) return
    setOpen(true)
    const startIdx = selectedIndex >= 0
      ? selectedIndex
      : options.findIndex(o => !o.disabled)
    setActiveIndex(startIdx)
  }, [disabled, selectedIndex, options])

  const closeMenu = useCallback(() => {
    setOpen(false)
    setActiveIndex(-1)
  }, [])

  const toggleMenu = useCallback(() => {
    if (open) closeMenu()
    else openMenu()
  }, [open, openMenu, closeMenu])

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const triggerRect = triggerRef.current.getBoundingClientRect()
    const menuMaxHeight = 280
    const spaceBelow = window.innerHeight - triggerRect.bottom
    const spaceAbove = triggerRect.top
    if (spaceBelow < menuMaxHeight && spaceAbove > spaceBelow) {
      setDirection('up')
    } else {
      setDirection('down')
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onDocDown(e) {
      if (
        triggerRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      ) return
      closeMenu()
    }
    document.addEventListener('mousedown', onDocDown)
    return () => document.removeEventListener('mousedown', onDocDown)
  }, [open, closeMenu])

  useEffect(() => {
    if (!open || activeIndex < 0 || !menuRef.current) return
    const node = menuRef.current.querySelector(`[data-index="${activeIndex}"]`)
    if (node) node.scrollIntoView({ block: 'nearest' })
  }, [open, activeIndex])

  function nextEnabled(from, dir) {
    const n = options.length
    if (n === 0) return -1
    let i = from
    for (let k = 0; k < n; k++) {
      i = (i + dir + n) % n
      if (!options[i].disabled) return i
    }
    return -1
  }

  function handleKeyDown(e) {
    if (disabled) return

    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === ' ' || e.key === 'Enter') {
        e.preventDefault()
        openMenu()
      }
      return
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      closeMenu()
      triggerRef.current?.focus()
      return
    }
    if (e.key === 'Tab') {
      closeMenu()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(prev => nextEnabled(prev < 0 ? -1 : prev, 1))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(prev => nextEnabled(prev < 0 ? options.length : prev, -1))
      return
    }
    if (e.key === 'Home') {
      e.preventDefault()
      setActiveIndex(nextEnabled(-1, 1))
      return
    }
    if (e.key === 'End') {
      e.preventDefault()
      setActiveIndex(nextEnabled(options.length, -1))
      return
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (activeIndex >= 0 && !options[activeIndex].disabled) {
        commitChoice(options[activeIndex].value)
      }
      return
    }

    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const buf = typeBufferRef.current
      buf.buffer = (buf.buffer + e.key).toLowerCase()
      clearTimeout(buf.timer)
      buf.timer = setTimeout(() => { buf.buffer = '' }, 500)
      const idx = options.findIndex(o =>
        !o.disabled && String(o.label).toLowerCase().startsWith(buf.buffer)
      )
      if (idx >= 0) setActiveIndex(idx)
    }
  }

  function commitChoice(val) {
    onChange?.(val)
    closeMenu()
    triggerRef.current?.focus()
  }

  const hasValue = selectedOption != null
  const displayLabel = hasValue ? selectedOption.label : placeholder

  return (
    <div className={`fd-select ${className}`} style={style}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className="fd-select__trigger"
        onClick={toggleMenu}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={`fd-select__value ${hasValue ? '' : 'fd-select__value--placeholder'}`}>
          {displayLabel}
        </span>
        <ChevronIcon />
      </button>

      {open && (
        <ul
          ref={menuRef}
          className={`fd-select__menu fd-select__menu--${direction}`}
          role="listbox"
          tabIndex={-1}
        >
          {options.length === 0 ? (
            <li className="fd-select__empty">Keine Einträge</li>
          ) : options.map((opt, idx) => {
            const isSelected = idx === selectedIndex
            const isActive = idx === activeIndex
            const cls = [
              'fd-select__option',
              isActive ? 'fd-select__option--active' : '',
              isSelected ? 'fd-select__option--selected' : '',
              opt.disabled ? 'fd-select__option--disabled' : '',
            ].filter(Boolean).join(' ')

            return (
              <li
                key={String(opt.value) + ':' + idx}
                data-index={idx}
                className={cls}
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled || undefined}
                onMouseEnter={() => !opt.disabled && setActiveIndex(idx)}
                onMouseDown={(e) => {
                  e.preventDefault()
                  if (!opt.disabled) commitChoice(opt.value)
                }}
              >
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {opt.label}
                </span>
                {isSelected && <CheckIcon />}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
